import React, { useState } from "react"
import Project from "../../models/project"
import Placement from "../../models/placement"
import Sensor, { earliestPlacementDate, excelDataForSensor } from "../../models/sensor"
import { Box, Tab, Tabs, Typography } from "@mui/material"
import { LoadingButton } from "@mui/lab"
import TemperatureChart from "../charts/TemperatureChart"
import TemperatureDifferenceChart from "../charts/TemperatureDifferenceChart"
import MaturityChart from "../charts/MaturityChart"
import StrengthChart from "../charts/StrengthChart"
import ChartDateSelectionView from "./ChartDateSelectionView"
import { maximumSampleDateBetweenDates } from "../../utilities/samples"
import moment, { Moment } from "moment"
import { useIsDesktop } from "../../utilities/environment"
import { useSearchParams } from "react-router-dom"
import ReportMenu from "../reusable/ReportMenu"
import { generateTemperatureDifferentialReport, generateSelectedSensorsReport } from "../reports/reportPdfGenerator"
import { logExport } from "../../utilities/analytics"
import xlsx from "xlsx"

interface Props {
  project: Project
  placement: Placement
  selectedSensors: Sensor[]
  colorMap: Map<number, string>
  startDate: moment.Moment
  endDate: moment.Moment
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props

  return (
    <Box
      flexDirection="column"
      sx={{
        width: "100%",
        height: "100%",
      }}
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        children
      )}
    </Box>
  )
}

const ChartTabs = (props: Props) => {
  if (props.selectedSensors.length === 0) {
    return (
      <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-around" sx={{
        width: "100%",
        height: "100%"
      }}>
        Select one or more sensors to view their data.
      </Box>
    )
  }

  const [searchParams, setSearchParams] = useSearchParams()

  const selectedTab = parseInt(searchParams.get("chart") ?? "0")
  const setSelectedTab = (newValue: number) => {
    setSearchParams(params => {
      params.set("chart", newValue.toString())
      return params
    })
  }

  if (props.placement.maturityMethod.type === "disabled" && selectedTab > 0) {
    setSelectedTab(0)
  }
  if (props.placement.maturityMethod.curveFit.type === "disabled" && selectedTab > 1) {
    setSelectedTab(0)
  }

  const earliestSensorPlacementDate: Moment | null = earliestPlacementDate(props.selectedSensors)
  const maximumSensorSampleDate: Moment | null = maximumSampleDateBetweenDates(props.selectedSensors, props.startDate, props.endDate)

  if (!earliestSensorPlacementDate || !maximumSensorSampleDate) {
    return (
      <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-around" sx={{
        width: "100%",
        height: "100%"
      }}>
        No data available for selected sensors.
      </Box>
    )
  }

  const usingCustomRange = searchParams.get("custom_range") !== null

  const getDateFromSearchParams = (position: number): moment.Moment => {
    const defaultDate = position === 0 ? earliestSensorPlacementDate : maximumSensorSampleDate
    const date = searchParams.get("custom_range")?.split(";")[position]
    return date ? moment(date) : defaultDate
  }

  const setDateInSearchParams = (position: number, date: moment.Moment) => {
    setSearchParams(params => {
      const dates = params.get("custom_range")?.split(";") ?? []
      dates[position] = date.format()
      params.set("custom_range", dates.join(";"))
      return params
    })
  }

  const textFieldStartDate = usingCustomRange ? getDateFromSearchParams(0) : earliestSensorPlacementDate
  const setTextFieldStartDate = (date: moment.Moment) => {
    setDateInSearchParams(0, date)
  }

  const textFieldEndDate = usingCustomRange ? getDateFromSearchParams(1) : maximumSensorSampleDate
  const setTextFieldEndDate = (date: moment.Moment) => {
    setDateInSearchParams(1, date)
  }

  const setUsingCustomRange = (enabled: boolean) => {
    setSearchParams(params => {
      if (enabled) {
        params.set("custom_range", textFieldStartDate?.toISOString(true) + ";" + textFieldEndDate?.toISOString(true))
      } else {
        params.delete("custom_range")
      }
      return params
    })
  }

  const chartStartDate = usingCustomRange ? textFieldStartDate : earliestSensorPlacementDate
  const chartEndDate = usingCustomRange ? textFieldEndDate : maximumSensorSampleDate
  const isDesktop = useIsDesktop()
  const totalHeight = isDesktop ? "100%" : "1000px"
  const preferredChartHeight = selectedTab === 0 ? "876px" : "100dvh"

  const [menuElement, setMenuElement] = useState<null | HTMLElement>(null)
  const menuIsOpen = Boolean(menuElement)
  const [isGeneratingReport, setIsGeneratingReport] = useState(false)

  const handleReportMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuElement(event.currentTarget)
  }

  const reportButtonDisabled = props.project.userInfo.permission === "readonly"

  const sanitizeForExcel = (input: string, maxLength?: number): string => {
    const allowedCharsRegex = /[a-zA-Z0-9.() _]/

    const sanitized = Array.from(input)
      .map(char => allowedCharsRegex.test(char) ? char : "_")
      .join("")

    return maxLength ? sanitized.slice(0, maxLength) : sanitized
  }

  const generateExcelFilename = (placementName: string): string => {
    const sanitized = sanitizeForExcel(placementName)
    const timestamp = new Date().toISOString().replace(/[-:]/g, "").slice(0, 15)
    return `${sanitized}${timestamp}`
  }

  const generateWorksheetTabName = (sensorName: string): string => {
    return sanitizeForExcel(sensorName, 31)
  }

  return (
    <Box sx={{
      padding: "12px",
      flexGrow: 1,
      height: totalHeight,
    }}>
      {props.placement.maturityMethod.type !== "disabled" &&
        <Tabs
          variant="fullWidth"
          value={selectedTab}
          onChange={(event, newValue) => setSelectedTab(newValue)}
        >
          <Tab label="Temperature" value={0} />
          <Tab label="Maturity" value={1} />
          {props.placement.maturityMethod.curveFit.type !== "disabled" &&
            <Tab label="Strength" value={2} />
          }
        </Tabs>
      }
      <Box sx={{
        height: isDesktop ? "calc(100% - 124px)" : preferredChartHeight
      }}>
        <ChartDateSelectionView
          selectedTab={usingCustomRange ? 1 : 0}

          startDate={textFieldStartDate}
          onStartDateChange={(start) => {
            setTextFieldStartDate(start)
          }}
          endDate={textFieldEndDate}
          onEndDateChange={(end) => {
            setTextFieldEndDate(end)
          }}
          minStartDate={earliestSensorPlacementDate}
          maxEndDate={maximumSensorSampleDate}
          onTabChange={(tab: number) => {
            if (tab === 1) {
              setUsingCustomRange(true)
              setTextFieldEndDate(maximumSensorSampleDate)
              setTextFieldStartDate(earliestSensorPlacementDate)
            } else {
              setUsingCustomRange(false)
            }
          }}
        />
        <LoadingButton
          loading={isGeneratingReport}
          loadingPosition="start"
          onClick={handleReportMenuClick}
          disabled={reportButtonDisabled || isGeneratingReport}
          sx={{
            float: "right",
            marginTop: isDesktop ? "0px" : "12px",
            height: "38px",
          }}
        >
          <Typography variant="subtitle2" color={reportButtonDisabled || isGeneratingReport ? "gray" : "primary"}>
            Report...
          </Typography>
        </LoadingButton>
        <ReportMenu
          open={menuIsOpen}
          menuElement={menuElement}
          onClose={() => {
            setMenuElement(null)
          }}
          onTemperatureDifferentialReportSelected={async () => {
            setMenuElement(null)
            setIsGeneratingReport(true)
            try {
              await generateTemperatureDifferentialReport(
                props.project,
                props.placement,
                props.selectedSensors,
                props.colorMap,
                chartStartDate,
                chartEndDate
              )
            } finally {
              setIsGeneratingReport(false)
            }
          }}
          onSelectedSensorsReportSelected={async () => {
            setMenuElement(null)
            setIsGeneratingReport(true)
            try {
              await generateSelectedSensorsReport(
                props.project,
                props.placement,
                props.selectedSensors,
                props.colorMap,
                chartStartDate,
                chartEndDate
              )
            } finally {
              setIsGeneratingReport(false)
            }
          }}
          onExportToExcelSelected={() => {
            if (props.placement === undefined) {
              return
            }
            const workbook = xlsx.utils.book_new()
            props.selectedSensors.forEach(sensor => {
              const sheet = xlsx.utils.json_to_sheet(excelDataForSensor(sensor, props.placement, chartStartDate, chartEndDate), { skipHeader: true })
              const sheetName = generateWorksheetTabName(sensor.name ?? sensor.serial)
              xlsx.utils.book_append_sheet(workbook, sheet, sheetName)
            })
            const filename = generateExcelFilename(props.placement.name) + ".xlsx"
            xlsx.writeFile(workbook, filename)
            logExport("excel", props.selectedSensors.length)
          }}
          selectedSensorsCount={props.selectedSensors.length}
        //totalSensorCount={props.sensors.length}
        />
        <TabPanel value={selectedTab} index={0}>
          <Box sx={{
            width: "100%",
            height: props.selectedSensors.length > 1 ? "60%" : "100%",
            padding: "12px"
          }}>
            <TemperatureChart
              forReport={false}
              startDate={chartStartDate}
              endDate={chartEndDate}
              sensors={props.selectedSensors}
              temperatureUnit={props.placement.temperatureUnit}
              colorMap={props.colorMap}
              timezone={props.placement.timeZone}
              min={props.placement.temperatureLimit?.minimumTemperature ?? undefined}
              max={props.placement.temperatureLimit?.maximumTemperature ?? undefined}
              projectId={props.project.id}
              placementId={props.placement.id}
            />
          </Box>
          {props.selectedSensors.length > 1 &&
            <Box flex={1} sx={{
              width: "100%",
              height: "40%",
              padding: "12px"
            }}>
              <TemperatureDifferenceChart
                forReport={false}
                sensors={props.selectedSensors}
                temperatureUnit={props.placement.temperatureUnit}
                colorMap={props.colorMap}
                start={chartStartDate}
                end={chartEndDate}
                placement={props.placement}
                threshold={props.placement.temperatureLimit?.difference ?? undefined}
              />
            </Box>
          }
        </TabPanel>
        <TabPanel value={selectedTab} index={1}>
          <Box sx={{
            width: "100%",
            height: "100%",
            padding: "12px"
          }}>
            <MaturityChart
              startDate={chartStartDate}
              endDate={chartEndDate}
              sensors={props.selectedSensors}
              placement={props.placement}
              colorMap={props.colorMap}
            />
          </Box>
        </TabPanel>
        {props.placement.maturityMethod.curveFit.type !== "disabled" &&
          <TabPanel value={selectedTab} index={2}>
            <Box sx={{
              width: "100%",
              height: "100%",
              padding: "12px"
            }}>
              <StrengthChart
                sensors={props.selectedSensors}
                placement={props.placement}
                colorMap={props.colorMap}
                startDate={chartStartDate}
                endDate={chartEndDate}
              />
            </Box>
          </TabPanel>
        }
      </Box>
    </Box>
  )
}

export default ChartTabs
